<template>
  <div class="card">
    <div class="card-header">
      <h2 class="card-title d-flex align-items-center">
        <i class="bx bx-dock-top mr-3" />
        Capacity for Grid Supply Plus and Smart Export Programs
      </h2>
    </div>
    <div v-if="error" class="card-body card-danger">
      <div class="d-flex align-items-center">
        <i class="bx bxs-error-circle mr-2" aria-hidden="true" />
        <span>Error loading capacity data</span>
      </div>
    </div>
    <div v-else-if="loading" class="card-body">
      <div class="d-flex align-items-center">
        <i class="bx bx-loader-alt bx-spin mr-2" aria-hidden="true" />
        <em>Loading capacity data...</em>
      </div>
    </div>
    <div v-else-if="!capacity" class="card-body">
      <div class="d-flex align-items-center">
        <i class="bx bx-error mr-2" aria-hidden="true" />
        <span>Capacity information unavailable</span>
      </div>
    </div>
    <div v-else class="card-body p-0">
      <table class="capacity-table table table-card-padding border-bottom-0">
        <thead class="thead-small">
          <tr>
            <th>Program</th>
            <th class="text-right">
              Total (kW)
            </th>
            <th class="pr-5 text-right">
              Available (kW)
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in capacity" :key="row.caption">
            <td>{{ row.caption }}</td>
            <td class="text-right">
              {{ row.total }}
            </td>
            <td class="pr-5 text-right">
              <span :title="row.tooltip">{{ row.available }}</span>
              <GuBadge :variant="row.badge" class="ml-3" />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue'
import { GuBadge } from '@shared/gu-components/badge'
import { _get } from '@shared/libs/lodash'

import { triggerLegacyRule } from '@projects/api/projects.api'

import { formatNumber } from '@shared/utils/number/formatNumber'

const ruleId = ref(_get(window, 'pageVars.configDashboard.hecoCitCapacityRule'))
const capacity = ref(null)

const {
  data: capacityData,
  isPending: loading,
  error,
} = triggerLegacyRule({ ruleId: ruleId.value }, { gcTime: 0 })

function processCapacity (response) {
  if (!response.totalCapacity || !response.totalCapacity.length) return null

  return response.totalCapacity.map(row => {
    const total = Number(row.value)
    const remaining = response.remainingCapacity.find(r => r.caption === row.caption) || {}
    let available = Number(remaining.value) || 0
    if (available < 0) available = 0

    return {
      caption: row.caption,
      total: formatNumber(total),
      available: formatNumber(Math.round(available)),
      tooltip: formatNumber(available),
      badge: getVariantForCapacity(available, total),
    }
  })
}

// generate a badge color for the available capacity
// 0 - danger - 10% - warning - 20% - success - 100%
function getVariantForCapacity (available, total) {
  const WARNING_THRESHOLD = 20
  const DANGER_THRESHOLD = 10
  const percent = Math.round(available * 100 / total)

  if (percent < DANGER_THRESHOLD) return 'danger'
  else if (percent < WARNING_THRESHOLD) return 'warning'
  else return 'success'
}

watch(capacityData, (newData) => {
  if (newData) capacity.value = processCapacity(newData)
})
</script>

<style lang="scss">
.capacity-table {
  thead th {
    border-top: none;
    border-bottom: none;
    background-color: #d3e2e3;
    font-size: .8rem;
    letter-spacing: 0;
  }

  tr:last-child {
    td:first-child {
      border-bottom-left-radius: 0.25rem;
    }
    td:last-child {
      border-bottom-right-radius: 0.25rem;
    }
  }

  tr:nth-child(2n) {
    background-color: #f2f2f2;
  }
}
</style>
